import React, { useEffect, useState } from 'react';

import { useMutation } from 'react-query';
import { withStyles } from '@material-ui/core/styles';
import MuiIconButton from '@material-ui/core/IconButton';

import { useForm, FormProvider } from 'react-hook-form';

import MuiBox from '@material-ui/core/Box';
import MuiTable from '@material-ui/core/Table';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiCollapse from '@material-ui/core/Collapse';
import MuiKeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MuiKeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MuiTypography from '@material-ui/core/Typography';
import MuiTextField from '@material-ui/core/TextField';
import MuiAddIcon from '@material-ui/icons/Add';
import Decimal from 'decimal.js';
import { WithTooltip } from 'utils/wrappers';

import RowButtons from './RowButtons';
import SubtableRow from './SubtableRow';

import * as API from 'api';

import _sortBy from 'lodash/sortBy';

const PlatformFees = API.PlatformFees();

const styles = () => ({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  edit: {
    fontSize: 'medium',
  },
  expandedRow: {
    backgroundColor: '#f3f3f3',
  },
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  buttonCell: {
    whiteSpace: 'nowrap',
  },
  totalsTable: {
    marginTop: '1rem',
  },
  fileCell: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  totalsCell: {
    border: 0,
    fontWeight: 'bold',
  },
  error: {
    color: 'red',
  },
});

const AddButton = ({ showAddRow, setShowAddRow }) => {
  if (!showAddRow) {
    return (
      <WithTooltip tooltip="Add new Platform Fee" key="edit">
        <MuiIconButton onClick={() => setShowAddRow(true)}>
          <MuiAddIcon />
        </MuiIconButton>
      </WithTooltip>
    );
  }

  return null;
};

const Row = props => {
  const {
    classes,
    row,
    refetch,
    newFeePercentage,
    setNewFeePercentage,
    newFeeStartInclusive,
    setNewFeeStartInclusive,
    newFeeEndInclusive,
    setNewFeeEndInclusive,
  } = props;

  const [platformFeeEditId, setPlatformFeeEditId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);

  const patchPlatformFeeMutation = useMutation(data => PlatformFees.patchFee(platformFeeEditId, data), {
    onSuccess: () => {
      refetch();
    },
    onError: error => {
      alert(error);
    },
    mutationKey: ['patchFee', platformFeeEditId],
  });

  const formMethods = useForm({ mode: 'onChange' });
  const { register } = formMethods;

  const createPlatformFeeMutation = useMutation(data => PlatformFees.createFee(data), {
    onSuccess: () => {
      refetch();
      setShowAddRow(false);
    },
    onError: error => {
      alert(error);
    },
  });
  const handleOnSave = () => {
    const createData = {
      asset_track_id: row.asset_track_id,
      percentage: new Decimal(newFeePercentage).toNumber(),
      effective_date_range: {
        start_inclusive: newFeeStartInclusive ? newFeeStartInclusive.toLowerCase() : '-infinity',
        end_inclusive: newFeeEndInclusive ? newFeeEndInclusive.toLowerCase() : 'infinity',
      },
    };
    createPlatformFeeMutation.mutate(createData);
  };

  const handlePatch = ({ percentage, startInclusive, endInclusive }) => {
    const patchData = {
      effective_date_range: {
        start_inclusive: startInclusive.toLowerCase(),
        end_inclusive: endInclusive.toLowerCase(),
      },
    };

    const oldPercentage = row.platformFees.find(({ id }) => id === platformFeeEditId)?.percentage;
    const sameAsBefore = new Decimal(oldPercentage).equals(percentage);

    if (percentage && !sameAsBefore) {
      patchData.percentage = new Decimal(percentage).toNumber();
    }

    patchPlatformFeeMutation.mutate(patchData);
  };

  const handleSubmit = e => {
    if (showAddRow && confirm(`Are you sure you want to create a new fee record?`)) {
      handleOnSave();
    } else if (
      platformFeeEditId !== null &&
      confirm(`Are you sure you want to edit the new fee record ID# ${platformFeeEditId}?`)
    ) {
      handlePatch(e);
    }
  };

  useEffect(() => {
    if (platformFeeEditId !== null) {
      setShowAddRow(false);
    }
  }, [platformFeeEditId, setShowAddRow]);

  useEffect(() => {
    if (showAddRow) {
      setPlatformFeeEditId(null);
    }
  }, [showAddRow, setPlatformFeeEditId]);

  // Because of a previous bug in the create release flow, some of these values are null
  const handleDisplayArtistName = () => {
    if (row.display_artist_name === null) {
      return '--';
    } else {
      return row.display_artist_name;
    }
  };

  return (
    <>
      <MuiTableRow className={classes.root}>
        <MuiTableCell>{row.asset_track_id}</MuiTableCell>
        <MuiTableCell>{handleDisplayArtistName()}</MuiTableCell>
        <MuiTableCell>{row.isrc_code}</MuiTableCell>
        <MuiTableCell>{row.title}</MuiTableCell>
        <MuiTableCell>{row.asset_release_upc}</MuiTableCell>
        <MuiTableCell style={{ width: '20%' }} className={classes.buttonCell} align="right">
          <MuiIconButton aria-label="expand row" onClick={() => setIsExpanded(!isExpanded)} tabIndex={-1}>
            {isExpanded ? <MuiKeyboardArrowUpIcon /> : <MuiKeyboardArrowDownIcon />}
          </MuiIconButton>
        </MuiTableCell>
      </MuiTableRow>
      <MuiTableRow className={classes.expandedRow}>
        <MuiTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <MuiCollapse in={isExpanded} timeout="auto" unmountOnExit>
            <MuiBox m={1}>
              <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
                  <MuiTypography variant="h6" gutterBottom component="div">
                    Platform Fees
                  </MuiTypography>
                  <MuiTable size="small" aria-label="platform fees">
                    <MuiTableHead>
                      <MuiTableRow>
                        <MuiTableCell style={{ width: '5%' }}>id</MuiTableCell>
                        <MuiTableCell style={{ width: '15%' }}>%</MuiTableCell>
                        <MuiTableCell style={{ width: '20%' }}>Start Date</MuiTableCell>
                        <MuiTableCell style={{ width: '20%' }}>End date</MuiTableCell>
                      </MuiTableRow>
                    </MuiTableHead>
                    <MuiTableBody>
                      {_sortBy(row.platformFees, fee => fee.effective_date_range.start_inclusive).map(fee => (
                        <SubtableRow
                          patchPlatformFeeMutation={patchPlatformFeeMutation}
                          key={fee.id}
                          fee={fee}
                          register={register}
                          platformFeeEditId={platformFeeEditId}
                          setPlatformFeeEditId={setPlatformFeeEditId}
                        />
                      ))}
                      {showAddRow && (
                        <MuiTableRow>
                          <MuiTableCell style={{ width: '5%' }} component="th" scope="row">
                            -
                          </MuiTableCell>
                          <MuiTableCell style={{ width: '15%' }}>
                            <MuiTextField
                              variant="outlined"
                              label="percentage"
                              size="small"
                              type="text"
                              placeholder={'0.00'}
                              value={newFeePercentage}
                              name="createPercentage"
                              onChange={e => setNewFeePercentage(e.target.value)}
                            />
                          </MuiTableCell>
                          <MuiTableCell style={{ width: '20%' }}>
                            <MuiTextField
                              variant="outlined"
                              label="start range"
                              size="small"
                              type="text"
                              placeholder={'YYYY-MM-DD'}
                              value={newFeeStartInclusive}
                              name="createStartInclusive"
                              onChange={e => setNewFeeStartInclusive(e.target.value)}
                            />
                          </MuiTableCell>
                          <MuiTableCell style={{ width: '20%' }}>
                            <MuiTextField
                              variant="outlined"
                              label="end range"
                              size="small"
                              type="text"
                              placeholder={'Infinity'}
                              value={newFeeEndInclusive}
                              name="endStartInclusive"
                              onChange={e => setNewFeeEndInclusive(e.target.value)}
                            />
                          </MuiTableCell>
                          <MuiTableCell align="right">
                            <RowButtons
                              isEditing={showAddRow}
                              setPlatformFeeEditId={setShowAddRow}
                              resetSubmitState={() => setShowAddRow(true)}
                              isLoading={createPlatformFeeMutation.isLoading}
                              isError={createPlatformFeeMutation.isError}
                            />
                          </MuiTableCell>
                        </MuiTableRow>
                      )}
                      <MuiTableRow>
                        <AddButton showAddRow={showAddRow} setShowAddRow={setShowAddRow} />
                      </MuiTableRow>
                    </MuiTableBody>
                  </MuiTable>
                </form>
              </FormProvider>
            </MuiBox>
          </MuiCollapse>
        </MuiTableCell>
      </MuiTableRow>
    </>
  );
};

export default withStyles(styles)(Row);
